import { MailArrowForwardRegular } from "@/pages/taskpane/components/icons/IconCustom";
import Button from "@/pages/taskpane/components/ui/Button";
import { useOutlookStore } from "@/pages/taskpane/lib/office/stores/outlookStore";
import { CheckSuitableForMovingOrderEnum } from "@/pages/taskpane/lib/order/types/Enums";
import {
    ISaveMovingOrderTempRequest,
    IViewMovingOrderRequest
} from "@/pages/taskpane/lib/order/types/OrderEntity";
import { ORDER_FORM_CONSTANTS } from "@/pages/taskpane/shared/constants/orderFormConstants";
import {
    useAppConfigStore,
    useAppointmentStore,
    useAttendeesStore,
    useResourceStore,
    useSettingStore,
    useThemeStore,
} from "@/pages/taskpane/shared/stores";
import { PreSelectedResourceInfo } from "@/pages/taskpane/shared/types/preSelectedResource";
import React, { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { dateTimeToTicks } from "../../resources/ulti";
import { useMoveOrder } from "../hooks/useMoveOrder";

type Props = {
  notifyInfo?: PreSelectedResourceInfo;
};

const MoveOrder = ({ notifyInfo }: Props) => {
  const { isDarkMode } = useThemeStore();
  const { t } = useTranslation();

  // Service hook - container layer
  const { onYesMoveOrder, onNoMoveOrder, onViewOrder } = useMoveOrder();

  // Gather data from stores
  const { startDateTimeUtc, endDateTimeUtc, timeZone } = useAppointmentStore();
  const { subject, iCalUId, customUid, recurringDates } = useOutlookStore();
  const { language, parameters } = useAppConfigStore();
  const { dateFormat, timeFormat } = useSettingStore();
  const { mainAttendee } = useAttendeesStore();
  const { originalSelectedResources } = useResourceStore();

  const onYes = () => {
    if (!notifyInfo || !notifyInfo.resources || notifyInfo.resources.length === 0 || !originalSelectedResources) {
      return;
    }
    // Build request object from stores data
    const request: ISaveMovingOrderTempRequest = {
      StartTime: dateTimeToTicks(new Date(startDateTimeUtc)),
      EndTime: dateTimeToTicks(new Date(endDateTimeUtc)),
      ResourceId: notifyInfo.resources[0].id || 0,
      ResourceIds: originalSelectedResources[0].id || 0,
      Subject: subject || "",
      OrganizerEmail: mainAttendee?.email || "",
      IsRecurring: recurringDates && recurringDates.length > 0 ? true : false,
      Uid: customUid || "",
      TimeZoneCode: timeZone || "",
      // only for move order reservation temp
      OldWorkflowId: customUid || "",
      OrganizerName: mainAttendee?.name || "",
      ResourceEmails: notifyInfo.resources[0].childResources.length
        ? notifyInfo.resources[0].childResources.map((childResource) => childResource.email).join(";")
        : "",
    };
    // Call service to open order form
    onYesMoveOrder(notifyInfo, request);
  };

  const onNo = () => {
    if (!notifyInfo || !notifyInfo.resources || notifyInfo.resources.length === 0 || !originalSelectedResources) {
      return;
    }
    onNoMoveOrder(notifyInfo, customUid || "");
  };

  const onView = () => {
    if (!notifyInfo || !notifyInfo.resources || notifyInfo.resources.length === 0 || !originalSelectedResources) {
      return;
    }
    const request: IViewMovingOrderRequest = {
      Language: language || "",
      TimeZoneName: timeZone || "",
      Guid: customUid || "",
      NewResourceId: notifyInfo.resources[0].id || 0,
      NewStartDate: dateTimeToTicks(new Date(startDateTimeUtc)).toString(),
      clientTimeFormat: timeFormat || "",
      clientDateFormat: dateFormat || "",
      clientSeparatorDate: "-",
      clientCultureCode: language,
    };
    onViewOrder(request);
  };

  const message = useMemo(() => {
    if (notifyInfo?.movingOrder) {
      switch (notifyInfo?.movingOrder?.movingOrderType) {
        case CheckSuitableForMovingOrderEnum.DeadlineStopMoving:
          return (
            <Trans
              i18nKey={ORDER_FORM_CONSTANTS[CheckSuitableForMovingOrderEnum.DeadlineStopMoving]}
              components={{ 1: <br /> }}
            />
          );
        case CheckSuitableForMovingOrderEnum.Deadline:
          if (parameters.stopMovingOrder) {
            return (
              <Trans
                i18nKey={ORDER_FORM_CONSTANTS[CheckSuitableForMovingOrderEnum.DeadlineStopMoving]}
                components={{ 1: <br /> }}
              />
            );
          }
          return (
            <>
              <Trans
                i18nKey={ORDER_FORM_CONSTANTS[CheckSuitableForMovingOrderEnum.Deadline]}
                components={{ 1: <br /> }}
              />
              <p className="text-xs font-normal text-gray-900 dark:text-white">
                {t(ORDER_FORM_CONSTANTS.AdditionalMsgYesDeadline)}
              </p>
              <p className="text-xs font-normal text-gray-900 dark:text-white">
                {t(ORDER_FORM_CONSTANTS.AdditionalMsgNoDeadline)}
              </p>
            </>
          );
        case CheckSuitableForMovingOrderEnum.TotalSuitable:
          return (
            <>
              {t(ORDER_FORM_CONSTANTS[CheckSuitableForMovingOrderEnum.TotalSuitable])}
              <p className="text-xs font-normal text-gray-900 dark:text-white">
                {t(ORDER_FORM_CONSTANTS.AdditionalMessage)}
              </p>
            </>
          );
        case CheckSuitableForMovingOrderEnum.PartialSuitable:
          return (
            <>
              <Trans
                i18nKey={ORDER_FORM_CONSTANTS[CheckSuitableForMovingOrderEnum.PartialSuitable]}
                components={{ 1: <br /> }}
              />
              <p className="text-xs font-normal text-gray-900 dark:text-white">
                {t(ORDER_FORM_CONSTANTS.AdditionalMsgYes)}
              </p>
              <p className="text-xs font-normal text-gray-900 dark:text-white">
                {t(ORDER_FORM_CONSTANTS.AdditionalMsgNo)}
              </p>
            </>
          );
        case CheckSuitableForMovingOrderEnum.Holiday:
          return (
            <>
              <Trans
                i18nKey={ORDER_FORM_CONSTANTS[CheckSuitableForMovingOrderEnum.Holiday]}
                components={{ 1: <br /> }}
              />
              <p className="text-xs font-normal text-gray-900 dark:text-white">
                {t(ORDER_FORM_CONSTANTS.AdditionalMessage)}
              </p>
            </>
          );
        case CheckSuitableForMovingOrderEnum.NotSupport:
          return (
            <>
              <Trans
                i18nKey={ORDER_FORM_CONSTANTS[CheckSuitableForMovingOrderEnum.NotSupport]}
                components={{ 1: <br /> }}
              />
            </>
          );
        default:
          return "";
      }
    }
    return notifyInfo?.message;
  }, [notifyInfo]);

  const renderButtons = useMemo(() => {
    if (
      notifyInfo?.movingOrder?.movingOrderType === CheckSuitableForMovingOrderEnum.DeadlineStopMoving ||
      notifyInfo?.movingOrder?.movingOrderType === CheckSuitableForMovingOrderEnum.Holiday
    ) {
      return (
        <>
          <Button
            className="bg-primaryBlue text-white w-[100%] h-[32px] dark:bg-brightBlue dark:text-black text-xs"
            label="Yes"
            onClick={onYes}
          />
          <Button
            className="bg-white text-black border border-lightGray w-[100%] h-[32px] dark:text-white dark:border-darkMediumGray text-xs"
            label="No"
            onClick={onNo}
          />
        </>
      );
    }
    return (
      <>
        <Button
          className="bg-primaryBlue text-white w-[100%] h-[32px] dark:bg-brightBlue dark:text-black text-xs"
          label="Yes"
          onClick={onYes}
        />
        <Button
          className="bg-white text-black border border-lightGray w-[100%] h-[32px] dark:text-white dark:border-darkMediumGray text-xs"
          label="No"
          onClick={onNo}
        />
        <Button
          className="bg-white text-black border border-lightGray w-[100%] h-[32px] dark:text-white dark:border-darkMediumGray text-xs"
          label="View Order"
          onClick={onView}
        />
      </>
    );
  }, [notifyInfo]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-2">
        <div>
          <MailArrowForwardRegular isDarkMode={isDarkMode} />
        </div>
        <div className="flex flex-col gap-2 w-full">
          <span className="text-sm font-medium text-gray-900 dark:text-white">{notifyInfo?.resources[0].name || ""}</span>
          <span className="text-xs font-normal text-gray-900 dark:text-white">{message}</span>
          <div className="flex justify-start mt-4 gap-3">{renderButtons}</div>
        </div>
      </div>
    </div>
  );
};

export default MoveOrder;
